import { composeStyles, Link, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';
import { useScreenPosition } from './useScreenPosition';

export interface GrowingElementProps
  extends PropsWithClassProps<
    'main' | 'topContainer' | 'topElement' | 'topClip' | 'bottomContainer' | 'bottomElement'
  > {
  link?: string;
  showInitially?: boolean;
  lastScroll?: number;
  topSlot?: React.ReactNode;
  /** Rendered above topSlot but outside its rounded clip, for elements that overflow the image (e.g. progress dot) */
  topOverlaySlot?: React.ReactNode;
  bottomSlot?: React.ReactNode;
  openInTab?: boolean;
  onClick?: () => void;
  onInViewChange?: (inView: boolean) => void;
}

// Progress bar for topOverlaySlot: its bottom corners follow the image's rounded-lg clip.
// Elliptical radius (8px wide, 4px tall) keeps the 4px-high bar inside the 8px image corner.
export const growingElementProgressClassProps = {
  root: 'absolute bottom-0 inset-x-0 z-10 rounded-b-[8px_4px] transition-[border-radius] duration-200 canhover:group-hover:rounded-b-none',
  bar: 'rounded-bl-[8px_4px] canhover:group-hover:rounded-bl-none',
};

export const GrowingElement = ({
  link,
  topSlot = null,
  topOverlaySlot = null,
  bottomSlot = null,
  showInitially,
  lastScroll,
  openInTab,
  onClick,
  onInViewChange,
  classProps,
}: GrowingElementProps) => {
  const { ref: inViewRef, inView } = useInView({
    rootMargin: '50px 800px',
  });

  const { domElement, screenPosition } = useScreenPosition(lastScroll);

  const itemVisible = useMemo(() => inView || showInitially, [inView, showInitially]);

  useEffect(() => {
    if (onInViewChange) {
      onInViewChange(inView);
    }
  }, [inView, onInViewChange]);

  const screenPositionStyle = {
    left: 'canhover:sm:group-hover:-right-10',
    center: 'canhover:sm:group-hover:-left-5 canhover:sm:group-hover:-right-5',
    right: 'canhover:sm:group-hover:-left-10',
  };

  const defaultStyles: GrowingElementProps['classProps'] = {
    root: 'group relative min-w-72 min-h-64 md:min-h-0 w-full grid group h-full canhover:hover:z-30',
    main: twMerge('overflow-visible', showInitially || itemVisible ? '' : 'content-auto'),
    topContainer: 'relative aspect-video w-full',
    topElement: twMerge(
      'absolute inset-0 transition-all duration-200 canhover:group-hover:-top-4',
      screenPositionStyle[screenPosition || 'center'],
    ),
    topClip:
      'absolute inset-0 overflow-hidden rounded-lg transition-[border-radius] duration-200 canhover:group-hover:rounded-b-none',
    bottomContainer: 'relative h-18 md:h-20',
    bottomElement: twMerge(
      'absolute inset-0 overflow-hidden rounded-b-lg bg-surface/0 transition-all duration-150 canhover:group-hover:-bottom-24 canhover:group-hover:bg-surface/80 backdrop-blur-xs',
      screenPositionStyle[screenPosition || 'center'],
    ),
  };

  const styles = composeStyles(defaultStyles, classProps);

  return (
    <Link
      href={link}
      noLink={false}
      classProps={{
        root: styles.root,
      }}
      onClick={() => {
        onClick?.();
      }}
      target={openInTab ? '_blank' : '_self'}
      ref={inViewRef}
    >
      {itemVisible && (
        <div
          ref={domElement}
          className={styles.main}
          style={{ willChange: 'left, right', containIntrinsicSize: 'auto 242px' }}
        >
          <div className={styles.topContainer}>
            <div className={styles.topElement} style={{ willChange: 'top' }}>
              <div className={styles.topClip}>{topSlot}</div>
              {topOverlaySlot}
            </div>
          </div>
          <div className={styles.bottomContainer}>
            <div className={styles.bottomElement} style={{ willChange: 'bottom' }}>
              {bottomSlot}
            </div>
          </div>
        </div>
      )}
    </Link>
  );
};
